import { AxiosResponse } from 'axios';
import qs from 'qs';
// eslint-disable-next-line import/no-duplicates
import { format, parseISO } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { ptBR } from 'date-fns/locale';
import { PAGE_SIZE } from '~/constants';
import { ContractView, Invoice, InvoiceView, Pagination } from '~/types';
import api from '~/utils/api';
import { formatCurrency } from '~/utils/formatCurrency';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export function addFormattedPropertiesToInvoice(invoice: Invoice): InvoiceView {
  return {
    ...invoice,
    nu_fatura: invoice.nu_fatura ?? '-',
    contratos: [],
    formattedValue: formatCurrency(Number(invoice.vr_fatura)),
    formattedStatus:
      invoice.st_pagamento === '1'
        ? 'Pago'
        : invoice.st_pagamento === '2'
        ? 'Vencida'
        : 'Em aberto',
    colorScheme:
      invoice.st_pagamento === '1'
        ? 'green'
        : invoice.st_pagamento === '2'
        ? 'red'
        : 'purple',
    formattedDueDate: formatInTimeZone(
      `${invoice.dt_vencimentofatura}T00:00:00Z`,
      'dd/MM/yyyy',
    ),
    formattedIssueDate: invoice.dt_emissaofatura
      ? format(parseISO(invoice.dt_emissaofatura), 'MMM/yyyy', { locale: ptBR })
      : '-',
    status: Number(invoice.st_pagamento),
    formattedValoresContratos: invoice?.contrato_fatura_consolidada?.map(it => {
      return formatCurrency(Number(it[0]));
    }),
    formattedValoresNumeroContratos: invoice?.contrato_fatura_consolidada?.map(
      it => {
        const [valor, numero] = it.split(', ');
        const lista = [formatCurrency(Number(valor)), numero.trim()];
        return lista;
      },
    ),
  };
}

export async function getInvoices(
  page: number,
  search = '',
  invoiceStatus: string,
  periodo_inicial: string,
  periodo_final: string,
  orderby: string,
  order: string,
  query?: any,
  pageSize = PAGE_SIZE,
): Promise<Pagination<InvoiceView>> {
  const { data } = await api.get<Pagination<Invoice>>('/faturas', {
    params: {
      page,
      size: pageSize,
      orderby,
      order,
      ...(search && { search }),
      ...(invoiceStatus ? { st_pagamento: invoiceStatus } : {}),
      ...(periodo_inicial && { periodo_inicial }),
      ...(periodo_final && { periodo_final }),
      ...{ ...query },
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  const invoices = data.results.map(invoice =>
    addFormattedPropertiesToInvoice(invoice),
  );

  return {
    ...data,
    results: invoices,
  };
}

export async function getInvoiceById(id: number): Promise<InvoiceView> {
  const { data } = await api.get<Invoice>(`/faturas/${id}`);

  const invoice = addFormattedPropertiesToInvoice(data);
  const contracts = data.contratos?.map(contract => ({
    ...contract,
    formattedInitialValue: formatCurrency(
      Number(contract.vr_totalinicialcontrato),
    ),
    formattedInitialDate: formatInTimeZone(
      `${contract.dt_inicialcontrato}T00:00:00Z`,
      'dd/MM/yyyy',
    ),
    formattedMensalValue: formatCurrency(
      Number(contract.vr_totalmensalcontrato),
    ),
  }));

  return {
    ...invoice,
    contratos: contracts as ContractView[],
  };
}

export async function getInvoicePdf(cd_fatura: string): Promise<AxiosResponse> {
  return api.get('/faturas/download', {
    params: {
      cd_fatura,
    },
    headers: {
      'Content-Type': 'application/pdf',
    },
    // responseType: 'blob',
  });
}

export async function getInvoicesXlsx(
  search = '',
  invoiceStatus: string,
  periodo_inicial: string,
  periodo_final: string,
  orderby: string,
  order: string,
  query?: QueryFilterContext,
): Promise<AxiosResponse<any>> {
  const url = '/faturas/xlsx';
  const groups = query?.groups?.map(e => e.value);
  const headoffices = query?.headOffices?.map(e => e.value);
  const branches = query?.branches?.map(e => e.value);
  return api.get(url, {
    params: {
      ...(search && { search }),
      ...(invoiceStatus ? { st_pagamento: invoiceStatus } : {}),
      ...(orderby && { orderby }),
      ...(order && { order }),
      ...(groups && { groups }),
      ...(headoffices && { headoffices }),
      ...(branches && { branches }),
      ...(periodo_inicial && { periodo_inicial }),
      ...(periodo_final && { periodo_final }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}
